import { useState } from "react";
import { Link, useLocation } from "wouter";
import {
  Shield, Users, BookOpen, ExternalLink, Zap, BarChart3, Link2,
  Facebook, Youtube, Phone, Mail, ChevronRight, UserCheck,
  Eye, EyeOff, Loader2, AlertCircle
} from "lucide-react";
import { trpc } from "@/lib/trpc";

const PRICING_TIERS = [
  {
    name: "Lite",
    setup: "$199",
    monthly: "$199",
    color: "#84ab4a",
    features: ["1 Vertical", "Basic Pipeline", "Rep Dashboard", "Referral Links"],
  },
  {
    name: "Standard",
    setup: "$799",
    monthly: "$499",
    color: "#1A6FD4",
    popular: true,
    features: ["3 Verticals", "Full Pipeline", "Commission Tracking", "Marketing Materials", "Wibiz Sync"],
  },
  {
    name: "Pro",
    setup: "$1,999",
    monthly: "$899",
    color: "#E8620A",
    features: ["All 6 Verticals", "Full Pipeline", "Agency Hierarchy", "Priority Support", "Custom Automations", "Wibiz Sync"],
  },
  {
    name: "Enterprise",
    setup: "Ask",
    monthly: "Ask",
    color: "#9B59B6",
    features: ["Specialized Automations", "Custom Integrations", "White-label Options", "Dedicated Support"],
  },
];

function TeamMemberLoginBox() {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loginMutation = trpc.teamMembers.login.useMutation({
    onSuccess: (data) => {
      // Store session and navigate to team dashboard
      sessionStorage.setItem("bc360_team_session", JSON.stringify(data.member));
      navigate("/team");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  };

  return (
    <div
      className={`group bg-[#0D1E35] border rounded-2xl cursor-pointer transition-all duration-200 ${
        expanded
          ? "border-[#6366F1]/60 bg-[#112040]"
          : "border-[#6366F1]/20 hover:border-[#6366F1]/60 hover:bg-[#112040]"
      }`}
    >
      {/* Header row — always visible */}
      <div
        className="flex items-center gap-4 p-6"
        onClick={() => !expanded && setExpanded(true)}
      >
        <div className="w-12 h-12 rounded-xl bg-[#6366F1]/12 border border-[#6366F1]/25 flex items-center justify-center flex-shrink-0">
          <UserCheck className="w-6 h-6 text-[#6366F1]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-white text-base mb-0.5">Team Member Login</div>
          <div className="text-sm text-white/40">
            Internal staff access — leads, pipeline &amp; assigned contacts
          </div>
        </div>
        {!expanded && (
          <ExternalLink className="w-4 h-4 text-[#6366F1]/50 group-hover:text-[#6366F1] transition-colors flex-shrink-0" />
        )}
      </div>

      {/* Inline login form — shown when expanded */}
      {expanded && (
        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-0 space-y-3">
          <div className="border-t border-white/8 mb-4" />
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Work email"
              required
              autoFocus
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#6366F1]/60 focus:ring-1 focus:ring-[#6366F1]/30 transition-colors"
            />
          </div>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 pr-10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#6366F1]/60 focus:ring-1 focus:ring-[#6366F1]/30 transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {loginMutation.isError && (
            <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Email or password is incorrect.
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setExpanded(false); loginMutation.reset(); }}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-sm text-white/40 hover:text-white/70 hover:border-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="flex-1 py-2.5 rounded-xl bg-[#6366F1] hover:bg-[#5254cc] disabled:bg-[#6366F1]/50 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            >
              {loginMutation.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</>
              ) : (
                "Sign In"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-[#0A1628] flex flex-col">
      {/* Background glows */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#84ab4a]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#1A6FD4]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 flex flex-col items-center px-6 py-12">
        <div className="w-full max-w-4xl">

          {/* Header */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center mb-5">
              <img
                src="https://bc360.wibiz.ai/wp-content/uploads/2026/03/bc360.png"
                alt="BizConnect360"
                className="h-16 object-contain"
              />
            </div>
            <p className="text-sm text-white/40 uppercase tracking-widest mb-1">
              Scale360 · Wibiz Dashboard · Internal Access
            </p>
            <div className="flex items-center justify-center gap-4 mt-3 text-xs text-white/30">
              <a href="tel:9793236599" className="flex items-center gap-1.5 hover:text-white/60 transition-colors">
                <Phone className="w-3 h-3" /> 979-323-6599
              </a>
              <span className="text-white/10">|</span>
              <a href="mailto:info@BizConnect360.com" className="flex items-center gap-1.5 hover:text-white/60 transition-colors">
                <Mail className="w-3 h-3" /> info@BizConnect360.com
              </a>
            </div>
          </div>

          {/* Portal Cards */}
          <div className="grid gap-4 mb-8">
            <Link href="/admin">
              <div className="group bg-[#0D1E35] border border-[#84ab4a]/20 rounded-2xl p-6 cursor-pointer hover:border-[#84ab4a]/60 hover:bg-[#112040] transition-all duration-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#84ab4a]/12 border border-[#84ab4a]/25 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-[#84ab4a]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-base mb-0.5">Admin Dashboard</div>
                    <div className="text-sm text-white/40">
                      Full pipeline, rep management, commissions, Wibiz sync &amp; all verticals
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#84ab4a]/50 group-hover:text-[#84ab4a] transition-colors flex-shrink-0" />
                </div>
              </div>
            </Link>

            <Link href="/rep">
              <div className="group bg-[#0D1E35] border border-[#1A6FD4]/20 rounded-2xl p-6 cursor-pointer hover:border-[#1A6FD4]/60 hover:bg-[#112040] transition-all duration-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1A6FD4]/12 border border-[#1A6FD4]/25 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[#1A6FD4]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-base mb-0.5">Rep / Agent Dashboard</div>
                    <div className="text-sm text-white/40">
                      Personal pipeline, referral links, commissions &amp; marketing materials
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#1A6FD4]/50 group-hover:text-[#1A6FD4] transition-colors flex-shrink-0" />
                </div>
              </div>
            </Link>

            {/* Team Member Login — inline expandable form */}
            <TeamMemberLoginBox />

            <Link href="/ghl-guide">
              <div className="group bg-[#0D1E35] border border-[#E8620A]/20 rounded-2xl p-6 cursor-pointer hover:border-[#E8620A]/60 hover:bg-[#112040] transition-all duration-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E8620A]/12 border border-[#E8620A]/25 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-6 h-6 text-[#E8620A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-base mb-0.5">Wibiz Setup Guide</div>
                    <div className="text-sm text-white/40">
                      Step-by-step setup: webhooks, pipelines, custom fields &amp; automations
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#E8620A]/50 group-hover:text-[#E8620A] transition-colors flex-shrink-0" />
                </div>
              </div>
            </Link>

            <Link href="/agent-registration-guide">
              <div className="group bg-[#0D1E35] border border-[#84ab4a]/20 rounded-2xl p-6 cursor-pointer hover:border-[#84ab4a]/60 hover:bg-[#112040] transition-all duration-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#84ab4a]/12 border border-[#84ab4a]/25 flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-6 h-6 text-[#84ab4a]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-base mb-0.5">Agent Registration Guide</div>
                    <div className="text-sm text-white/40">
                      Flow overview, email templates &amp; hierarchy rules for onboarding agents
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#84ab4a]/50 group-hover:text-[#84ab4a] transition-colors flex-shrink-0" />
                </div>
              </div>
            </Link>
          </div>

          {/* Feature strip */}
          <div className="grid grid-cols-3 gap-3 mb-10">
            {[
              { icon: BarChart3, label: "7-Stage Pipeline", color: "#84ab4a" },
              { icon: Link2, label: "Auto Referral Links", color: "#1A6FD4" },
              { icon: Zap, label: "Live Wibiz Sync", color: "#E8620A" },
            ].map(({ icon: Icon, label, color }) => (
              <div key={label} className="bg-[#0D1E35] border border-white/5 rounded-xl p-3 text-center">
                <Icon className="w-4 h-4 mx-auto mb-1.5" style={{ color }} />
                <div className="text-xs text-white/50">{label}</div>
              </div>
            ))}
          </div>

          {/* Pricing Section */}
          <div className="mb-10">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-white font-['Syne',sans-serif] mb-1">Scale360 Pricing</h2>
              <p className="text-sm text-white/40">Setup fee + monthly subscription</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PRICING_TIERS.map((tier) => (
                <div
                  key={tier.name}
                  className={`relative bg-[#0D1E35] rounded-2xl p-5 border transition-all ${
                    tier.popular ? "border-[#1A6FD4]/50 shadow-lg shadow-[#1A6FD4]/10" : "border-white/8"
                  }`}
                >
                  {tier.popular && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#1A6FD4] text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider">
                      Most Popular
                    </div>
                  )}
                  <div className="text-center mb-4">
                    <div className="text-sm font-bold mb-2" style={{ color: tier.color }}>{tier.name}</div>
                    <div className="text-white/80 text-xs mb-0.5">
                      <span className="text-lg font-bold text-white">{tier.setup}</span> setup
                    </div>
                    <div className="text-white/50 text-xs">
                      <span className="text-base font-semibold text-white/70">{tier.monthly}</span>/mo
                    </div>
                  </div>
                  <ul className="space-y-1.5">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-xs text-white/50">
                        <ChevronRight className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: tier.color }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {tier.name === "Enterprise" && (
                    <div className="mt-3 pt-3 border-t border-white/8">
                      <p className="text-[10px] text-white/30 text-center leading-relaxed">
                        Scale360 offers Enterprise Solutions for Specialized Automations &amp; Customizations.{" "}
                        <a href="mailto:info@BizConnect360.com" className="text-[#9B59B6] hover:underline">
                          Schedule a complimentary scope of work call
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Commission Grid Preview */}
          <div className="bg-[#0D1E35] border border-white/8 rounded-2xl p-5 mb-8">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#84ab4a]" />
              Agent Commission Structure
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/8">
                    <th className="text-left py-2 pr-4 text-white/40 font-medium">Level</th>
                    <th className="text-center py-2 px-3 text-white/40 font-medium">Setup</th>
                    <th className="text-center py-2 px-3 text-white/40 font-medium">Monthly</th>
                    <th className="text-left py-2 pl-3 text-white/40 font-medium">Applies To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr>
                    <td className="py-2.5 pr-4 font-semibold text-[#84ab4a]">Agency</td>
                    <td className="py-2.5 px-3 text-center text-white font-bold">30–40%</td>
                    <td className="py-2.5 px-3 text-center text-white font-bold">30–40%</td>
                    <td className="py-2.5 pl-3 text-white/50">All verticals, no year-2 declines</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-4 font-semibold text-[#1A6FD4]">Senior Associate</td>
                    <td className="py-2.5 px-3 text-center text-white font-bold">25–30%</td>
                    <td className="py-2.5 px-3 text-center text-white font-bold">25–30%</td>
                    <td className="py-2.5 pl-3 text-white/50">All verticals, no year-2 declines</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 pr-4 font-semibold text-[#E8620A]">Associate</td>
                    <td className="py-2.5 px-3 text-center text-white font-bold">20%</td>
                    <td className="py-2.5 px-3 text-center text-white font-bold">20%</td>
                    <td className="py-2.5 pl-3 text-white/50">All verticals, no year-2 declines</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-white/25 mt-3">50% on setups &amp; subscriptions with no year-2 declines for qualifying partners.</p>
            <div className="mt-4 pt-4 border-t border-white/8">
              <div className="text-[10px] text-white/30 font-bold uppercase tracking-widest mb-2">Enrollment Fees</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <a href="https://api.wibiz.ai/payment-link/69b8cdf8942c7dd36efd0b07" target="_blank" rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-between gap-2 bg-[#84ab4a]/10 border border-[#84ab4a]/20 rounded-xl px-3 py-2 hover:bg-[#84ab4a]/20 transition-colors">
                  <span className="text-xs font-semibold text-[#84ab4a]">Fundable360 — $2,995</span>
                  <span className="text-[10px] text-[#84ab4a]/70 font-bold">Pay →</span>
                </a>
                <a href="https://api.wibiz.ai/payment-link/69b8ce33f35c540e14832dfb" target="_blank" rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-between gap-2 bg-[#C8920A]/10 border border-[#C8920A]/20 rounded-xl px-3 py-2 hover:bg-[#C8920A]/20 transition-colors">
                  <span className="text-xs font-semibold text-[#C8920A]">Legacy360 — $2,995</span>
                  <span className="text-[10px] text-[#C8920A]/70 font-bold">Pay →</span>
                </a>
              </div>
            </div>
          </div>

          {/* Client referral link */}
          <div className="text-center mb-6">
            <a
              href="/join"
              className="text-xs text-white/25 hover:text-white/50 transition-colors"
            >
              Client referral landing page → /join?ref=PARTNERID
            </a>
          </div>

          {/* Footer */}
          <div className="border-t border-white/8 pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Social Links */}
              <div className="flex items-center gap-3">
                <a
                  href="https://www.facebook.com/BizConnect360/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#1877F2] hover:border-[#1877F2]/40 transition-all"
                  title="Facebook"
                >
                  <Facebook className="w-3.5 h-3.5" />
                </a>
                <a
                  href="https://www.instagram.com/bizconnect360"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#E1306C] hover:border-[#E1306C]/40 transition-all"
                  title="Instagram"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                </a>
                <a
                  href="https://www.youtube.com/@BizConnect360"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#FF0000] hover:border-[#FF0000]/40 transition-all"
                  title="YouTube"
                >
                  <Youtube className="w-3.5 h-3.5" />
                </a>
                <a
                  href="https://www.tiktok.com/@bizconnect360"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-white/40 transition-all"
                  title="TikTok"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/>
                  </svg>
                </a>
              </div>

              {/* Legal Links */}
              <div className="flex items-center gap-4 text-[10px] text-white/25">
                <a
                  href="https://bizconnect360.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white/50 transition-colors"
                >
                  Privacy Policy
                </a>
                <span className="text-white/10">·</span>
                <a
                  href="https://bizconnect360.com/terms-of-service/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white/50 transition-colors"
                >
                  Terms of Service
                </a>
                <span className="text-white/10">·</span>
                <span>© 2026 BizConnect360</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
